import { useState, useEffect, ReactNode } from 'react';
import { LockClosedIcon, WrenchScrewdriverIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

// Read from environment — disabled by default
const GATE_ENABLED = import.meta.env.VITE_CONSTRUCTION_GATE_ENABLED === 'true';
const CONSTRUCTION_ACCESS_CODE = import.meta.env.VITE_CONSTRUCTION_PIN || '';

interface ConstructionGateProps {
  children: ReactNode;
}

export function ConstructionGate({ children }: ConstructionGateProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);

  // Check if already authenticated from session storage
  useEffect(() => {
    if (!GATE_ENABLED) return;
    const constructionAuth = sessionStorage.getItem('construction_authenticated');
    if (constructionAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Skip gate entirely if not enabled
  if (!GATE_ENABLED) {
    return <>{children}</>;
  }

  const handlePinSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pinInput === CONSTRUCTION_ACCESS_CODE) {
      setIsAuthenticated(true);
      sessionStorage.setItem('construction_authenticated', 'true');
      setPinError(false);
    } else {
      setPinError(true);
      setPinInput('');
    }
  };

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPinInput(value);
    setPinError(false);
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-mono-950 flex items-center justify-center">
        <div className="w-full max-w-sm">
          <div className="border border-mono-800 rounded-lg bg-mono-900/50 p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-4">
                <WrenchScrewdriverIcon className="h-8 w-8 text-cyan-400" />
              </div>
              <h1 className="text-xl font-bold text-mono-100 tracking-wider">UNDER CONSTRUCTION</h1>
              <p className="text-xs text-mono-500 mt-2">Enter access code to preview</p>
            </div>

            <form onSubmit={handlePinSubmit}>
              <div className="mb-6">
                <input
                  type="password"
                  value={pinInput}
                  onChange={handlePinChange}
                  placeholder="------"
                  className={clsx(
                    "w-full text-center text-2xl tracking-[0.5em] font-mono py-4 bg-mono-900 border rounded-lg focus:outline-none focus:ring-1",
                    pinError
                      ? "border-red-500/50 text-red-400 focus:ring-red-500/50"
                      : "border-mono-700 text-mono-100 focus:ring-cyan-500/50 focus:border-cyan-500/50"
                  )}
                  autoFocus
                  maxLength={6}
                />
                {pinError && (
                  <p className="text-xs text-red-400 text-center mt-2">Invalid access code</p>
                )}
              </div>

              <button
                type="submit"
                disabled={pinInput.length !== 6}
                className={clsx(
                  "w-full py-3 text-xs tracking-widest rounded transition-colors",
                  pinInput.length === 6
                    ? "bg-cyan-500/20 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/30"
                    : "bg-mono-800 border border-mono-700 text-mono-500 cursor-not-allowed"
                )}
              >
                ACCESS PREVIEW
              </button>
            </form>

            <div className="mt-8 pt-6 border-t border-mono-800">
              <div className="flex items-center gap-3 text-mono-600">
                <LockClosedIcon className="h-4 w-4" />
                <p className="text-[10px] tracking-wider">
                  This site is in active development. Access is restricted.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
